import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, HelpCircle, MessageSquare, ClipboardList } from "lucide-react";
import { faqJsonLd } from "@/lib/seo.schemas";

const FAQS = [
  {
    q: "Hur fungerar prissättningen?",
    a: "Godkända grundpriser visas på respektive koncept och är exklusive moms. Mario Kart Cup och Racing Grand Prix har inget fastställt paketpris; omfattning och pris fastställs efter kontroll. Leverans specificeras separat i offerten.",
  },
  {
    q: "Vad ingår i grundpriset?",
    a: "Angiven utrustning, ordinarie personal, planering, rigg, nedtagning och stämningsljus ingår i det beskrivna godkända paketet. Inkluderade delar debiteras inte igen.",
  },
  {
    q: "Hur beräknas leveransen?",
    a: "Transport: 160 kr exkl. moms per faktiskt körd svensk mil från Kungsbacka. Alla körsträckor räknas och leveransen specificeras i offerten. Lastväg, åtkomst och hantering kontrolleras före bokning.",
  },
  {
    q: "När tillkommer extra arbete?",
    a: "Extra arbete får bara debiteras när det ligger utanför det beskrivna upplägget och har godkänts i förväg. Taxan är 650 kr exkl. moms per faktisk persontimme. Redan inkluderad personal eller rigg debiteras inte igen.",
  },
  {
    q: "Vad behöver ni veta före bokning?",
    a: "Datum, ort, lokal, deltagarantal och önskade tider. Vi kontrollerar tillträde, lastväg, el, säkerhetsyta och faktisk utrustning innan omfattning och tillgänglighet bekräftas.",
  },
  {
    q: "Kan vi välja flera koncept?",
    a: "Ni kan lägga aktuella koncept i förfrågan. Flera koncept och överenskomna tillval samordnas manuellt, så att omfattning, gemensamma resurser och pris blir tydliga före bokning.",
  },
  {
    q: "Hur länge pågår ett koncept?",
    a: "Tidsfönstret framgår av respektive koncept. Bemanning, pauser och turordning följer det beskrivna upplägget. Tid och gruppstorlek för Mario Kart Cup och Racing Grand Prix fastställs efter kontroll.",
  },
  {
    q: "Vad ordnar vi själva?",
    a: "Kundens ansvar framgår av respektive koncept. Lokal, el, tillträde och en hjälpare för lätt möblering är vanliga förutsättningar. Mat, dryck och möbler ingår inte om de inte uttryckligen anges.",
  },
  {
    q: "Hur fungerar Mario Kart Cup?",
    a: "Mario Kart Cup använder en fysisk, handstyrd bilbana med Mario Kart-bilar. Två deltagare kör per heat. Total tid, gruppstorlek, bemanning, yta och paketpris fastställs efter kontroll av den faktiska banan.",
  },
];

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Vanliga frågor — Event Sverige" },
      {
        name: "description",
        content: "Svar på de vanligaste frågorna om eventuthyrning, leverans, priser, bokning och koncept.",
      },
      { property: "og:title", content: "FAQ — Event Sverige" },
      {
        property: "og:description",
        content: "Vanliga frågor om att hyra eventutrustning från Event Sverige.",
      },
      { property: "og:url", content: "/faq" },
    ],
    links: [{ rel: "canonical", href: "https://eventsverige.se/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(faqJsonLd(FAQS)),
      },
    ],
  }),
  component: FaqPage,
});

function FaqPage() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 bg-grain opacity-20" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-22">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-6">
              <HelpCircle className="h-3.5 w-3.5" />
              FAQ
            </div>
            <h1 className="font-display text-5xl sm:text-6xl font-extrabold leading-[0.95]">
              Vanliga
              <br />
              <span className="text-accent">frågor & svar.</span>
            </h1>
            <p className="mt-6 text-lg text-primary-foreground/80 max-w-xl">
              Här samlar vi svaren på det vi får fråga om mest. Hittar du inte det du söker? Tveka inte att höra av dig.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ LIST */}
      <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="space-y-6">
          {FAQS.map((faq, i) => (
            <div key={i} className="rounded-2xl bg-card border border-border p-6 sm:p-8">
              <h2 className="font-display text-lg font-bold flex items-start gap-3">
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent text-xs font-bold mt-0.5">
                  {i + 1}
                </span>
                {faq.q}
              </h2>
              <p className="mt-3 text-muted-foreground leading-relaxed pl-9">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground p-10 sm:p-16">
          <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-accent/20 blur-3xl" aria-hidden />
          <div className="relative max-w-2xl mx-auto text-center">
            <h2 className="font-display text-3xl sm:text-5xl font-extrabold">Hittade du inte svaret?</h2>
            <p className="mt-4 text-primary-foreground/80 text-lg">
              Skicka ett meddelande eller ring oss, så hjälper vi dig planera ert event.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-bold hover:bg-accent/90"
              >
                <MessageSquare className="h-4 w-4" />
                Kontakta oss
              </Link>
              <Link
                to="/forfragan"
                className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/30 px-6 py-3 font-semibold hover:bg-primary-foreground/10"
              >
                <ClipboardList className="h-4 w-4" />
                Skicka förfrågan
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
